import { Component, Inject, inject, OnInit, Optional, ChangeDetectorRef } from '@angular/core';
import { analytics } from '@app/events/segment/analytics';
import {
  MatDialogContent,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ResumeUploadService } from '@app/services/api/resume-upload/resume-upload.service';
import { ResumeStatusService } from '@app/services/signals/resume-status-service';
import { Store } from '@ngrx/store';
import { setResumeUpload } from '@app/state/actions/resume.action';
import { convertFileToBase64 } from '@app/utils/sharedFunctions';
import { WarningSnackBarComponent } from '@app/shared/snack-bars/warning/warning.component';
import { WarningDefaultOptions } from '@app/shared/snack-bars/warning/warning.utils';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ResumeData, ResumeParseResponse, ResumeUploadResponse } from '@app/schemas/resume';

export interface AddResumeDialogData {
  mode?: 'new' | 'view';
  redirectTo?: string;
  section?: string;
}

export interface AddResumeDialogResult {
  resumeUploaded?: boolean;
}

@Component({
  selector: 'app-add-resume-dialog',
  imports: [
    MatDialogContent,
    MatButtonModule,
  ],
  templateUrl: './add-resume-dialog.component.html',
  styleUrl: './add-resume-dialog.component.css'
})
export class AddResumeDialogComponent implements OnInit {
  private _snackBar = inject(MatSnackBar);
  private sanitizer = inject(DomSanitizer);

  step: 'intro' | 'upload' | 'analyzing' | 'results' = 'intro';
  mode: 'new' | 'view' = 'new';
  allowedFileTypes = '.doc,.docx,.pdf,.txt';
  filename = '';
  resumeFile: File | undefined = undefined;
  isDragging = false;
  isUploading = false;
  isLoadingExisting = false;
  resumeLoadingMessage = '';
  uploadError: string | null = null;

  showReuploadConfirm = false;
  isSubmitting = false;

  // Resume results data
  resumeData: ResumeData | null = null;
  resumeFileUrl: string = '';
  resumePdfUrl: SafeResourceUrl | null = null;
  localPdfUrl: SafeResourceUrl | null = null;

  private section: string;

  constructor(
    private router: Router,
    private uploadApiService: ResumeUploadService,
    private resumeStatusService: ResumeStatusService,
    private store: Store,
    private dialogRef: MatDialogRef<AddResumeDialogComponent, AddResumeDialogResult>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: AddResumeDialogData | null,
    private cdr: ChangeDetectorRef,
  ) {
    this.section = this.dialogData?.section || 'explore';
  }

  ngOnInit() {
    if (this.dialogData?.mode === 'view') {
      this.mode = 'view';
      this.loadExistingResume();
    }
  }

  loadExistingResume() {
    this.step = 'results';
    this.isLoadingExisting = true;
    this.cdr.detectChanges();
    this.resumeLoadingMessage = 'Loading your resume…';

    this.uploadApiService.getResumeData().subscribe({
      next: (response) => {
        this.isLoadingExisting = false;
        this.resumeLoadingMessage = 'Resume loaded.';
        this.resumeData = {
          contactInfo: response.resumeData?.contactInfo || null,
          skills: response.resumeData?.skills || [],
          workHistory: response.resumeData?.workHistory || [],
          education: response.resumeData?.education || [],
          certifications: response.resumeData?.certifications || [],
        };
        this.resumeFileUrl = response.resumeFileUrl || '';
        if (this.resumeFileUrl) {
          this.setPreviewUrl(this.resumeFileUrl);
        }
        this.step = 'results';
      },
      error: () => {
        this.isLoadingExisting = false;
        // If no resume found or error, fall back to intro as new upload
        this.mode = 'new';
        this.step = 'intro';
      },
    });
  }

  get isDocxFile(): boolean {
    const ext = this.getFileExtension(this.filename || this.resumeFileUrl);
    return ext === 'docx' || ext === 'doc';
  }

  downloadResumeFile() {
    if (this.resumeFile) {
      // Download from local file when on results page (before submit)
      const blobUrl = URL.createObjectURL(this.resumeFile);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = this.filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } else if (this.resumeFileUrl) {
      // Download from S3 when viewing existing resume
      window.open(this.resumeFileUrl, '_blank');
    }
  }

  private setPreviewUrl(fileUrl: string) {
    const extension = this.getFileExtension(fileUrl);
    // Extract filename from URL for display
    if (!this.filename) {
      try {
        const pathname = new URL(fileUrl).pathname;
        this.filename = decodeURIComponent(pathname.split('/').pop() || '');
      } catch { /* ignore */ }
    }
    if (extension === 'docx' || extension === 'doc') {
      // No iframe preview for docx/doc — file thumbnail card shown instead
    } else {
      // PDF and TXT presigned URLs serve with correct content-type and inline disposition
      const pdfFragment = extension === 'pdf' ? '#navpanes=0' : '';
      this.resumePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl + pdfFragment);
    }
  }

  onClickStart() {
    analytics.track('Resume Start', { section: this.section });
    this.step = 'upload';
  }

  onClickBack() {
    if (this.step === 'upload') {
      analytics.track('Resume Upload Back', { section: this.section });
      this.step = 'intro';
      this.filename = '';
      this.resumeFile = undefined;
    } else if (this.step === 'results') {
      this.step = 'upload';
      this.resumeData = null;
      this.resumeFileUrl = '';
      this.resumePdfUrl = null;
      this.revokeLocalPdfUrl();
      this.filename = '';
      this.resumeFile = undefined;
    }
  }

  closeDialog() {
    analytics.track('Resume Exit', { section: this.section });
    this.revokeLocalPdfUrl();
    this.dialogRef.close();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const fileList = event.dataTransfer?.files;
    if (fileList && fileList.length > 0) {
      this.handleFileAndSubmit(fileList[0]);
    }
  }

  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.handleFileAndSubmit(target.files[0]);
    }
  }

  handleFileAndSubmit(file: File) {
    this.resumeFile = file;
    if (this.validateFile()) {
      this.filename = file.name;
      this.isUploading = true;
      this.resumeLoadingMessage = 'Uploading your resume…';
      analytics.track('Resume Upload Start', { section: this.section });
      this.createLocalPdfPreview(file);
      this.submitResume();
    }
  }

  createLocalPdfPreview(file: File) {
    this.revokeLocalPdfUrl();
    const extension = this.getFileExtension(file.name);
    if (extension === 'pdf') {
      const objectUrl = URL.createObjectURL(file);
      this.localPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl + '#navpanes=0');
    } else if (extension === 'txt') {
      this.buildTextPreview(file).then(url => {
        this.localPdfUrl = url;
      });
    }
    // docx/doc shows file thumbnail card instead of iframe preview
  }

  private async buildTextPreview(file: File): Promise<SafeResourceUrl> {
    const content = await file.text();
    return this.buildTextViewerUrl(content);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildTextViewerUrl(content: string): SafeResourceUrl {
    const html = `<!DOCTYPE html>
<html><head><style>
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 2rem;
  margin: 0;
  line-height: 1.6;
  color: #22333B;
  background: #fff;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 14px;
}
</style></head>
<body>${this.escapeHtml(content)}</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    return this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
  }

  private getFileExtension(filenameOrUrl: string): string {
    try {
      // Handle URLs
      if (filenameOrUrl.startsWith('http')) {
        const pathname = new URL(filenameOrUrl).pathname;
        return pathname.split('.').pop()?.toLowerCase() || '';
      }
    } catch { /* fall through */ }
    return filenameOrUrl.split('.').pop()?.toLowerCase() || '';
  }

  revokeLocalPdfUrl() {
    if (this.localPdfUrl) {
      // localPdfUrl is a SafeResourceUrl; we can't easily revoke it,
      // but the browser will GC the blob when the reference is dropped
      this.localPdfUrl = null;
    }
  }

  validateFile(): boolean {
    if (this.resumeFile) {
      const fileSizeMB = this.resumeFile.size / (1024 * 1024);
      if (fileSizeMB > 2) {
        this.uploadError = 'Sorry! Your file is larger than 2 MB.';
        analytics.track('Resume Parse Error', { section: this.section, error: 'File exceeds 2 MB' });
        this.resumeFile = undefined;
        return false;
      }

      const extension = this.resumeFile.name.split('.').pop()?.toLowerCase() || '';
      if (!this.allowedFileTypes.includes(extension)) {
        this.uploadError = 'Sorry! That file type is not supported.';
        analytics.track('Resume Parse Error', { section: this.section, error: 'Unsupported file type' });
        this.resumeFile = undefined;
        return false;
      }
    }
    this.uploadError = null;
    return true;
  }

  async submitResume() {
    if (!this.resumeFile) {
      this.showErrorMessage('File not selected');
      return;
    }

    const base64File = await convertFileToBase64(this.resumeFile);
    this.isUploading = false;
    this.step = 'analyzing';
    this.resumeLoadingMessage = 'Analyzing your resume…';

    this.uploadApiService.parseResume({
      base64file: base64File,
      filename: this.filename,
    }).subscribe({
      next: (response: ResumeParseResponse) => this.onParseSuccess(response),
      error: () => this.onParseError(),
    });
  }

  onParseSuccess(response: ResumeParseResponse) {
    analytics.track('Resume Parse Success', {
      section: this.section,
      skills_detected: response.resumeData?.skills || [],
    });

    this.resumeData = response.resumeData;
    this.step = 'results';
    this.resumeLoadingMessage = 'Resume analysis complete.';
  }

  onParseError() {
    analytics.track('Resume Parse Error', {
      section: this.section,
      error: 'Error while parsing file',
    });
    this.isUploading = false;
    this.step = 'upload';
    this.resumeLoadingMessage = 'Resume analysis failed. Please try again.';
    this.showErrorMessage('Error while parsing file');
    this.resumeFile = undefined;
    this.filename = '';
  }

  toggleReuploadConfirm() {
    this.showReuploadConfirm = !this.showReuploadConfirm;
  }

  onInfoMissingHover() {
    analytics.track('Resume Info Missing Hover', { section: this.section });
  }

  onSkillsHover() {
    analytics.track('Resume Skills Hover', { section: this.section });
  }

  onReupload() {
    analytics.track('Resume Reupload', { section: this.section });
    this.showReuploadConfirm = false;
    this.mode = 'new';
    this.step = 'upload';
    this.resumeData = null;
    this.resumeFileUrl = '';
    this.resumePdfUrl = null;
    this.revokeLocalPdfUrl();
    this.filename = '';
    this.resumeFile = undefined;
  }

  async onSubmitResults() {
    if (!this.resumeFile) {
      this.showErrorMessage('File not available');
      return;
    }

    analytics.track('Resume Submit', { section: this.section });
    this.isSubmitting = true;
    this.resumeLoadingMessage = 'Submitting your resume…';
    const base64File = await convertFileToBase64(this.resumeFile);

    this.uploadApiService.uploadResume({
      base64file: base64File,
      filename: this.filename,
    }).subscribe({
      next: () => {
        analytics.track('Resume Upload Success', { section: this.section, file_path: this.filename });
        this.resumeStatusService.setResumeStatusUpdated(true);
        this.store.dispatch(setResumeUpload({ resume_upload: true }));
        this.isSubmitting = false;
        this.resumeLoadingMessage = 'Resume submitted.';
        this.revokeLocalPdfUrl();
        this.dialogRef.close({ resumeUploaded: true });

        if (this.dialogData?.redirectTo) {
          this.router.navigate([this.dialogData.redirectTo]);
        }
      },
      error: () => {
        analytics.track('Resume Upload Error', { section: this.section, error: 'Error while submitting resume' });
        this.isSubmitting = false;
        this.resumeLoadingMessage = 'Resume submission failed. Please try again.';
        this.showErrorMessage('Error while submitting resume');
      },
    });
  }

  get pdfPreviewUrl(): SafeResourceUrl | null {
    // In 'new' mode, always use local blob preview; in 'view' mode, prefer S3 URL
    if (this.mode === 'new') {
      return this.localPdfUrl;
    }
    return this.resumePdfUrl || this.localPdfUrl;
  }

  get contactFields(): { label: string; warningLabel: string; found: boolean }[] {
    const info = this.resumeData?.contactInfo;
    return [
      { label: 'Name', warningLabel: 'Did you include your name?', found: !!info?.name?.full_name },
      { label: 'Email', warningLabel: 'Did you include your email?', found: !!(info?.email_addresses && info.email_addresses.length > 0) },
      { label: 'Phone', warningLabel: 'Did you share your phone number?', found: !!(info?.telephones && info.telephones.length > 0) },
      { label: 'Location', warningLabel: 'Did you share your location?', found: !!info?.location },
    ];
  }

  get educationCertCount(): number {
    return (this.resumeData?.education?.length || 0) + (this.resumeData?.certifications?.length || 0);
  }

  showErrorMessage(msg: string) {
    this._snackBar.openFromComponent(WarningSnackBarComponent, {
      ...WarningDefaultOptions,
      data: { message: msg },
      duration: 3000,
    });
  }

  private readonly MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    // Handle "MM/YYYY" format (e.g., "06/2022")
    const mmYyyy = dateStr.match(/^(\d{1,2})\/(\d{4})$/);
    if (mmYyyy) {
      const monthIndex = parseInt(mmYyyy[1], 10) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${this.MONTHS[monthIndex]} ${mmYyyy[2]}`;
      }
    }
    // Handle "MM/DD/YYYY" format
    const mmDdYyyy = dateStr.match(/^(\d{1,2})\/\d{1,2}\/(\d{4})$/);
    if (mmDdYyyy) {
      const monthIndex = parseInt(mmDdYyyy[1], 10) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${this.MONTHS[monthIndex]} ${mmDdYyyy[2]}`;
      }
    }
    // Handle ISO "YYYY-MM-DD" or "YYYY-MM"
    const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})/);
    if (isoMatch) {
      const monthIndex = parseInt(isoMatch[2], 10) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${this.MONTHS[monthIndex]} ${isoMatch[1]}`;
      }
    }
    // Already formatted or unrecognised — return as-is
    return dateStr;
  }

  formatDateRange(startDate: string | null, endDate: string | null): string {
    const start = this.formatDate(startDate);
    const end = this.formatDate(endDate);
    if (start && end) return `${start} \u2013 ${end}`;
    return start || end;
  }

  formatEducationDate(dateStr: string | null): string {
    const formatted = this.formatDate(dateStr);
    if (!formatted) return '';
    // Parse month/year back to check if it's in the future
    const monthIndex = this.MONTHS.indexOf(formatted.split(' ')[0]);
    const year = parseInt(formatted.split(' ')[1], 10);
    if (monthIndex >= 0 && year) {
      const dateValue = new Date(year, monthIndex);
      if (dateValue > new Date()) {
        return `Expected: ${formatted}`;
      }
    }
    return formatted;
  }
}
